import { z } from 'zod';

/**
 * Content library contract (spec §6). Tasks (prompts) and model answers are distinct entities.
 * A task file in `content/tasks/*.yaml` is validated against `ContentTaskFileSchema`.
 */

export const SOURCE_TYPES = [
  'official_sample',
  'published_practice',
  'original_ai_draft',
  'teacher_verified',
  'user_uploaded',
] as const;
export const SourceTypeSchema = z.enum(SOURCE_TYPES);
export type SourceType = z.infer<typeof SourceTypeSchema>;

export const REGISTERS = ['informal', 'formal', 'semi_formal'] as const;
export const RegisterSchema = z.enum(REGISTERS);
export type Register = z.infer<typeof RegisterSchema>;

export const REVIEW_STATUSES = ['unverified', 'teacher_reviewed', 'verified'] as const;
export const ReviewStatusSchema = z.enum(REVIEW_STATUSES);

export const TOPICS = [
  'friends_family_invitations',
  'holidays_travel',
  'school_language_classes',
  'requests_appointments_information',
  'workplace_everyday_admin',
  'complaints_incidents',
] as const;
export const TopicSchema = z.enum(TOPICS);
export type Topic = z.infer<typeof TopicSchema>;

export const LETTER_TYPES = ['email', 'letter', 'note', 'message', 'postcard'] as const;

export const ContentSourceSchema = z.object({
  /** Stable slug, e.g. `glc-a2-sample-2019` or `gn-original`. */
  key: z.string().regex(/^[a-z0-9][a-z0-9-]{1,63}$/),
  type: SourceTypeSchema,
  title: z.string().min(3),
  url: z.url().nullable().default(null),
  year: z.number().int().min(1990).max(2100).nullable().default(null),
  license: z.string().min(3),
  /** ISO date when a human verified the source text against the primary source; null = unverified. */
  verifiedAt: z.iso.date().nullable().default(null),
  notes: z.string().optional(),
});
export type ContentSource = z.infer<typeof ContentSourceSchema>;

export const WordLimitsSchema = z
  .object({
    min: z.number().int().min(1),
    max: z.number().int().min(1),
  })
  .refine((v) => v.max >= v.min, { message: 'max must be >= min' });

export const VocabularyItemSchema = z.object({
  el: z.string().min(1),
  ru: z.string().min(1),
});

export const ModelAnswerSchema = z.object({
  /** Exact UTF-8 Greek text. Paragraphs separated by a blank line. NFC-normalized on import. */
  textEl: z.string().min(20),
  translationRu: z.string().optional(),
  authoredBy: z.enum(['ai_draft', 'teacher', 'official']),
  reviewStatus: ReviewStatusSchema,
  notes: z.string().optional(),
});

export const ContentTaskSchema = z.object({
  /** Stable slug used for idempotent import, e.g. `invite-friend-birthday`. */
  key: z.string().regex(/^[a-z0-9][a-z0-9-]{2,79}$/),
  sourceKey: z.string(),
  /** Whether the *task prompt text* was verified against a primary source. */
  promptProvenance: z.enum(['verified_official', 'adapted_from_official', 'original']),
  level: z.enum(['A1', 'A2', 'B1']),
  register: RegisterSchema,
  letterType: z.enum(LETTER_TYPES),
  topic: TopicSchema,
  title: z.object({ el: z.string().min(2), ru: z.string().min(2) }),
  promptEl: z.string().min(10),
  promptRu: z.string().min(10),
  /** Optional bullet points the answer must cover (task fulfilment). */
  requiredPoints: z.array(z.object({ el: z.string(), ru: z.string() })).default([]),
  wordLimits: WordLimitsSchema,
  modes: z.array(z.enum(['copy', 'recall', 'free', 'exam'])).min(1),
  difficulty: z.number().int().min(1).max(5),
  tags: z.array(z.string().regex(/^[a-z0-9-]+$/)).default([]),
  learningGoals: z.array(z.string()).default([]),
  vocabulary: z.array(VocabularyItemSchema).default([]),
  copyrightNote: z.string().min(3),
  modelAnswer: ModelAnswerSchema.optional(),
});
export type ContentTask = z.infer<typeof ContentTaskSchema>;

/** One YAML/JSON file may hold sources and/or tasks. */
export const ContentFileSchema = z.object({
  sources: z.array(ContentSourceSchema).default([]),
  tasks: z.array(ContentTaskSchema).default([]),
});
export type ContentFile = z.infer<typeof ContentFileSchema>;
